import { applyShowDataFor } from 'utils/components/ShowDataFor/ShowDataFor';
import {
    mapFromElementRangeArray,
    TimeSeriesDisableRange,
} from 'utils/timeseries/TimeSeriesDisableRange';
import { v4 as uuid } from 'uuid';
import { FillsGraphDataState } from 'widgets/fillsGraph/store/data/FillsGraphDataState';
import { getFillsGraphData } from 'widgets/fillsGraph/fillsGraphData';
import isNil from 'lodash/isNil';
import {
    defaultFillsGraphConfiguration,
    FillsChartType,
    FillsGraphConfiguration,
} from 'widgets/fillsGraph/store/configuration/FillsGraphConfiguration';
import { PlCapsuleConfigState } from 'widgets/plCapsule/store/config/PlCapsuleConfigState';
import {
    FillsGraphPlChartItem,
    getFillsGraphPlDataFromTimeSeries,
} from 'widgets/fillsGraph/pl/fillsGraphPlData';
import cloneDeep from 'lodash/cloneDeep';

export interface PlData {
    breaksInTrading: TimeSeriesDisableRange[];
    plSeries: FillsGraphPlChartItem[];
    startTime: number;
    endTime: number;
    id: string;
    tickSize?: number | null;
    lastNotNilItem?: FillsGraphPlChartItem;
}

export function buildPlData(
    streamData: FillsGraphDataState | undefined,
    config: PlCapsuleConfigState
): PlData | undefined {
    const { plSeries, lastNotNilItem } = buildTimeSeries(streamData, config);

    if (!plSeries || plSeries.length === 0) {
        return undefined;
    }

    const breaksInTrading = mapFromElementRangeArray(streamData?.disableRange);
    const tickSize = streamData?.orderStat?.tickSize;

    return {
        id: uuid(),
        breaksInTrading,
        lastNotNilItem,
        plSeries,
        tickSize,
        startTime: plSeries[0].time,
        endTime: plSeries[plSeries.length - 1].time,
    };
}

function buildTimeSeries(
    streamData: FillsGraphDataState | undefined,
    config: PlCapsuleConfigState
): {
    plSeries?: FillsGraphPlChartItem[];
    lastNotNilItem?: FillsGraphPlChartItem;
} {
    if (!streamData?.timeSeries?.length) {
        return {};
    }

    const truncatedTimeSeries = applyShowDataFor(
        streamData.timeSeries,
        config.showDataFor
    );
    const data: FillsGraphDataState = {
        ...streamData,
        timeSeries: truncatedTimeSeries,
    };

    const fillsGraphConfig: FillsGraphConfiguration = {
        ...cloneDeep(defaultFillsGraphConfiguration),
        [FillsChartType.PnL]: {
            ...defaultFillsGraphConfiguration[FillsChartType.PnL],
            pnlChartBenchmark: config.pnlChartBenchmark,
        },
    };

    const fillsGraphData = getFillsGraphData(
        data,
        fillsGraphConfig,
        'B',
        undefined,
        undefined,
        undefined,
        undefined,
        []
    );

    const { data: timeSeries } = fillsGraphData;

    const plSeries = getFillsGraphPlDataFromTimeSeries(timeSeries);

    const withValues = plSeries.filter(
        (f) =>
            !isNil(f.realizedPnl) ||
            !isNil(f.unrealizedPnl) ||
            !isNil(f.totalPnl)
    );
    const lastNotNilTimeSeriesItem = withValues[withValues.length - 1];

    return {
        plSeries,
        lastNotNilItem: lastNotNilTimeSeriesItem,
    };
}
